import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { CommissionService } from '../../../shared/services/zones.service';
import {
  COMMISSION_SCOPE_LABEL,
  CommissionConfig,
  CommissionConfigHistory,
} from '../../../shared/types/zone.types';

@Component({
  selector: 'app-commission-config',
  imports: [
    CommonModule,
    LocalizedDatePipe,
    DecimalPipe,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
  ],
  templateUrl: './commission-config.component.html',
})
export class CommissionConfigComponent implements OnInit {
  private readonly service = inject(CommissionService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly config = signal<CommissionConfig | null>(null);

  readonly scopeLabel = COMMISSION_SCOPE_LABEL;

  // ── History ───────────────────────────────────────────────────────────
  readonly history = signal<CommissionConfigHistory[]>([]);
  readonly historyLoading = signal(false);
  readonly historyError = signal<string | null>(null);
  readonly historyPage = signal(1);
  readonly historyTotalPages = signal(0);
  readonly historyTotalItems = signal(0);
  readonly historySize = 20;

  readonly historyPageWindow = computed<number[]>(() => {
    const total = this.historyTotalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.historyPage();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  // ── Edit global rates modal ──────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editForm = this.fb.group({
    defaultTotalPct: [0, [Validators.required, Validators.min(0)]],
    defaultPlatformPct: [0, [Validators.required, Validators.min(0)]],
    defaultOperatorPct: [0, [Validators.required, Validators.min(0)]],
  });

  ngOnInit(): void {
    this.fetchConfig();
    this.loadHistory();
  }

  fetchConfig(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .getGlobal()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => { this.config.set(c); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.error.set(toErrorMessage(err, 'Impossible de charger la configuration.'));
        },
      });
  }

  loadHistory(): void {
    this.historyLoading.set(true);
    this.historyError.set(null);
    this.service
      .history(this.historyPage(), this.historySize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.history.set(res.data ?? []);
          this.historyTotalItems.set(res.totalItems ?? 0);
          this.historyTotalPages.set(res.totalPages ?? 0);
          this.historyLoading.set(false);
        },
        error: (err: unknown) => {
          this.history.set([]);
          this.historyTotalItems.set(0);
          this.historyTotalPages.set(0);
          this.historyLoading.set(false);
          this.historyError.set(toErrorMessage(err));
        },
      });
  }

  goToHistoryPage(p: number): void {
    if (p < 1 || p > this.historyTotalPages() || p === this.historyPage()) return;
    this.historyPage.set(p);
    this.loadHistory();
  }

  // ── Edit ─────────────────────────────────────────────────────────────
  openEdit(): void {
    const c = this.config();
    this.editForm.reset({
      defaultTotalPct: c?.defaultTotalPct ?? 0,
      defaultPlatformPct: c?.defaultPlatformPct ?? 0,
      defaultOperatorPct: c?.defaultOperatorPct ?? 0,
    });
    this.editError.set(null);
    this.editOpen.set(true);
  }
  closeEdit(): void {
    if (this.editSaving()) return;
    this.editOpen.set(false);
  }
  submitEdit(): void {
    if (this.editForm.invalid || this.editSaving()) {
      this.editForm.markAllAsTouched();
      return;
    }
    const raw = this.editForm.getRawValue();
    // Client-side sanity check: platform + operator should equal total.
    const sum = Number(raw.defaultPlatformPct) + Number(raw.defaultOperatorPct);
    if (Math.abs(sum - Number(raw.defaultTotalPct)) > 0.001) {
      this.editError.set(
        `Total (${raw.defaultTotalPct}%) doit être égal à plateforme (${raw.defaultPlatformPct}%) + opérateur (${raw.defaultOperatorPct}%) = ${sum}%.`,
      );
      return;
    }
    this.editSaving.set(true);
    this.editError.set(null);
    this.service
      .setGlobal({
        defaultTotalPct: raw.defaultTotalPct,
        defaultPlatformPct: raw.defaultPlatformPct,
        defaultOperatorPct: raw.defaultOperatorPct,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editSaving.set(false);
          this.editOpen.set(false);
          this.fetchConfig();
          this.historyPage.set(1);
          this.loadHistory();
        },
        error: (err: unknown) => {
          this.editSaving.set(false);
          this.editError.set(
            toErrorMessage(err, 'errors.updateFailed', {
              400: 'Validation backend : total = plateforme + opérateur ?',
            }),
          );
        },
      });
  }
}
